import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Minus, Wrench, X } from "lucide-react";
import { api, formatHours, today } from "@/lib/api";
import { toast } from "sonner";
import CapacityCalendar from "@/components/CapacityCalendar";

const STEP = 0.5;
const MIN = 0.5;
const MAX = 200;

/**
 * Reusable dialog for creating (POST) or editing (PUT) a repair.
 * Props:
 *   - initial: existing repair object -> edit mode
 *   - open, onOpenChange: controlled state (only used in edit mode)
 *   - onSaved: (repair) => void
 *   - trigger: optional trigger element (create mode)
 */
export default function AddRepairDialog({ initial = null, open: controlledOpen, onOpenChange, onCreated, onSaved, trigger, initialStartDate = null }) {
  const isEdit = !!initial;
  const isControlled = controlledOpen !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? controlledOpen : openState;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setOpenState;

  const [hours, setHours] = useState(1.0);
  const [description, setDescription] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [startDate, setStartDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [lookedUpPlate, setLookedUpPlate] = useState("");

  useEffect(() => {
    if (open && initial) {
      setHours(initial.estimated_hours || 1.0);
      setDescription(initial.description || "");
      setCustomerName(initial.customer_name || "");
      setLicensePlate(initial.license_plate || "");
      setBrand(initial.brand || "");
      setModel(initial.model || "");
      setPhone(initial.phone || "");
      setNotes(initial.notes || "");
      setStartDate(initial.segments?.[0]?.date || today());
    } else if (open && !initial) {
      setHours(1.0);
      setDescription("");
      setCustomerName("");
      setLicensePlate("");
      setBrand("");
      setModel("");
      setPhone("");
      setNotes("");
      setStartDate(initialStartDate || today());
    }
  }, [open, initial, initialStartDate]);

  const dec = () => setHours((h) => Math.max(MIN, +(h - STEP).toFixed(2)));
  const inc = () => setHours((h) => Math.min(MAX, +(h + STEP).toFixed(2)));

  // On create mode, when the user finishes typing a plate that already exists,
  // pre-fill customer name / phone / brand / model (only empty fields).
  const lookupPlate = async () => {
    if (isEdit) return;
    const plate = licensePlate.trim().toUpperCase();
    if (!plate || plate.length < 4 || plate === lookedUpPlate) return;
    setLookedUpPlate(plate);
    try {
      const { data } = await api.get(`/vehicles/${encodeURIComponent(plate)}/history`);
      const v = data?.vehicle;
      if (!v || !data.total_count) return;
      let filled = 0;
      if (v.customer_name && !customerName.trim()) { setCustomerName(v.customer_name); filled++; }
      if (v.phone && !phone.trim())               { setPhone(v.phone);               filled++; }
      if (v.brand && !brand.trim())               { setBrand(v.brand);               filled++; }
      if (v.model && !model.trim())               { setModel(v.model);               filled++; }
      if (filled > 0) {
        toast.success(`Cliente autocompletado desde ${plate}`, { duration: 2500 });
      }
    } catch {
      /* silent: unknown plate is fine */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { toast.error("Describe la avería"); return; }
    setSubmitting(true);
    try {
      const body = {
        description: description.trim(),
        customer_name: customerName.trim(),
        license_plate: licensePlate.trim().toUpperCase(),
        brand: brand.trim(),
        model: model.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
        estimated_hours: hours,
        start_date: startDate,
      };
      const res = isEdit
        ? await api.put(`/repairs/${initial.id}`, body)
        : await api.post("/repairs", body);
      const segs = res.data.segments || [];
      if (isEdit) {
        toast.success(segs.length > 1 ? `Cita actualizada en ${segs.length} días (dividida)` : "Cita actualizada");
      } else {
        toast.success(segs.length > 1 ? `Cita programada en ${segs.length} días (dividida)` : `Cita programada el ${segs[0]?.date}`);
      }
      setOpen(false);
      onCreated && onCreated(res.data);
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Error al actualizar" : "Error al programar"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && !isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button data-testid="add-repair-button" className="bg-blue-700 hover:bg-blue-800 text-white h-12 px-6">
              <Wrench className="w-4 h-4 mr-2" />
              Nueva Cita
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0 [&>button.absolute]:hidden" data-testid={isEdit ? "edit-repair-dialog" : "add-repair-dialog"}>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="exit-repair-dialog-btn"
          className="sticky top-0 z-10 w-full flex items-center justify-center gap-2 h-12 bg-slate-900 hover:bg-slate-800 text-white font-display font-bold text-base transition-colors rounded-t-lg"
        >
          <X className="w-5 h-5" />
          Salir
        </button>
        <div className="p-6 space-y-4">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Editar Cita" : "Nueva Cita · Mecánica General"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Cambia los datos. Si modificas las horas, se reprograma automáticamente." : "Describe la avería y asigna el tiempo estimado. Se programa automáticamente respetando la capacidad diaria."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="desc">Descripción de la avería *</Label>
            <Textarea id="desc" data-testid="repair-description-input" placeholder="Ej: Cambio de embrague, ruido en suspensión..."
              value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="min-h-[80px]" />
          </div>

          <div className="space-y-2">
            <Label>Tiempo estimado</Label>
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
              <Button type="button" data-testid="decrease-time-button" variant="outline" size="icon" onClick={dec} className="h-12 w-12 shrink-0">
                <Minus className="w-5 h-5" />
              </Button>
              <div className="flex-1 text-center" data-testid="repair-hours-display">
                <div className="text-3xl font-display font-bold text-slate-900">{formatHours(hours)}</div>
                <div className="text-xs text-slate-500">incrementos de 30 min</div>
              </div>
              <Button type="button" data-testid="increase-time-button" variant="outline" size="icon" onClick={inc} className="h-12 w-12 shrink-0">
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="customer">Nombre del cliente</Label>
              <Input id="customer" data-testid="repair-customer-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nombre" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input id="phone" data-testid="repair-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="600 000 000" className="h-12" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="plate">Matrícula</Label>
              <Input id="plate" data-testid="repair-plate-input" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} onBlur={lookupPlate} placeholder="0000ABC" className="h-12 uppercase" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand">Marca</Label>
              <Input id="brand" data-testid="repair-brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Seat" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Modelo</Label>
              <Input id="model" data-testid="repair-model-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="León" className="h-12" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas / comentarios</Label>
            <Textarea
              id="notes"
              data-testid="repair-notes-input"
              placeholder="Ej: Se cambió embrague el 10/06, disco nuevo, cliente avisado..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="min-h-[70px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="start">{isEdit ? "Reprogramar desde" : "Empezar desde"}</Label>
            <CapacityCalendar
              value={startDate}
              onChange={setStartDate}
              capacityEndpoint="/capacity"
              neededHours={0.5}
              testid="repair-start-date"
            />
            <p className="text-xs text-slate-500">
              {isEdit
                ? "Solo se reprograma si cambias las horas o esta fecha."
                : "Los días en rojo están llenos, son festivos o no laborables. La cita saltará al siguiente día disponible."}
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 w-full sm:w-auto" data-testid="cancel-repair-button">Cancelar</Button>
            <Button type="submit" disabled={submitting} className="h-12 w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-base" data-testid="submit-repair-button">
              {submitting ? (isEdit ? "Guardando..." : "Programando...") : (isEdit ? "Guardar cambios" : "Programar Cita")}
            </Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
